import { Box, Link, Tab, Tabs, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { api } from "../../api/API";
import { Task, TaskFilter } from "../../api/Tasks";
import { useSnackAction } from "../../misc/SnackAction";
import { useSyncedRef } from "../../misc/SyncedRef";
import { useTaskPages } from "../../misc/TaskPages";
import { PanelAddField } from "../task/panel/PanelSection";
import { TaskLinkSection, toLink } from "../task/panel/TaskLinkSection";

type TabKey = "pending" | "completed";

// PanelTaskTabs is the task list a project and a goal share: what is still to do, and
// what has been ticked off, a page at a time. The completed tab isn't asked for until
// it's opened, since anything that has been running a while has far more behind it
// than ahead of it.
export function PanelTaskTabs(props: {
    // scope is what the tabs list: one project's tasks, or one goal's
    scope: TaskFilter;

    addPlaceholder: string;

    // removeLabel names what the close button on a row does, since a list either ends
    // a task's membership of a project or of a goal
    removeLabel: string;

    // busy holds the rows while one of the caller's own actions is in flight
    busy: boolean;

    onAdd: (title: string) => void;
    onOpen: (task: Task) => void;
    onRemove: (task: Task) => void;

    // onReorder turns on drag-sorting, which only the pending tab offers: what has
    // been ticked off is read in the order it was done
    onReorder?: (ids: number[]) => void;

    // onPendingCount reports how many tasks are still open, which is more than the tab
    // has loaded
    onPendingCount?: (count: number) => void;
}) {
    const [tab, setTab] = useState<TabKey>("pending");
    const [openedCompleted, setOpenedCompleted] = useState(false);

    // the pending list is read whichever tab is on top, so its count stays live while
    // the completed one is being read
    const pending = useTaskPages({ ...props.scope, completed: false });
    const completed = useTaskPages(openedCompleted ? { ...props.scope, completed: true } : null);

    const list = tab === "pending" ? pending : completed;
    const byId = new Map(list.tasks.map(task => [task.id, task]));

    const onPendingCount = useSyncedRef(props.onPendingCount);
    useEffect(() => {
        if (onPendingCount.current) onPendingCount.current(pending.total);
    }, [pending.total, onPendingCount]);

    const toggle = useSnackAction(async (input: { id: number; completed: boolean }) => {
        await api.tasks.complete(input);
    }, { success: "Saved" });

    const archive = useSnackAction(async (input: { task: Task }) => {
        await api.tasks.upsert({
            ...input.task,
            archived: true,
        });
    }, { success: "Archived" });

    const busy = props.busy || toggle.loading || archive.loading;

    return (
        <>
            {/* full-bleed: the rule under the tabs runs the panel's width */}
            <Box sx={{ mx: -3, bgcolor: "grey.50", borderTop: 1, borderColor: "divider" }}>
                <Tabs
                    value={tab}
                    onChange={(_, value: TabKey) => {
                        setTab(value);
                        if (value === "completed") setOpenedCompleted(true);
                    }}
                    sx={{ px: 3, minHeight: 30, borderBottom: 1, borderColor: "divider" }}
                >
                    <Tab value="pending" sx={tabSx}
                         label={pending.total > 0 ? "Pending (" + pending.total + ")" : "Pending"} />
                    <Tab value="completed" sx={tabSx} label="Completed" />
                </Tabs>
            </Box>

            <TaskLinkSection
                value={list.tasks.map(toLink)}
                busy={busy}
                removeLabel={props.removeLabel}
                compact
                completedDates={tab === "completed"}
                onOpen={link => {
                    const task = byId.get(link.id);
                    if (task) props.onOpen(task);
                }}
                onToggle={(link, completed) => toggle.callback({ id: link.id, completed: completed })}
                onRemove={link => {
                    const task = byId.get(link.id);
                    if (task) props.onRemove(task);
                }}
                onArchive={link => {
                    const task = byId.get(link.id);
                    if (task) archive.callback({ task: task });
                }}
                onReorder={tab === "pending" ? props.onReorder : undefined}
            >
                {list.more && (
                    <Link
                        component="button"
                        type="button"
                        variant="caption"
                        underline="hover"
                        color="text.secondary"
                        onClick={list.loadMore}
                    >
                        Load more
                    </Link>
                )}

                {tab === "completed" && !completed.loading && completed.tasks.length === 0 && (
                    <Typography variant="caption" color="text.secondary">
                        Nothing has been ticked off yet
                    </Typography>
                )}

                {/* a task added from the completed tab would land on the other one, so
                    it's only offered where it can be seen */}
                {tab === "pending" && (
                    <PanelAddField placeholder={props.addPlaceholder} onAdd={props.onAdd} />
                )}
            </TaskLinkSection>

            {/* the pages of the tab that isn't on top are still watched, so the count
                on it stays live */}
            <Box sx={{ display: tab === "pending" ? undefined : "none" }}>{pending.element}</Box>
            <Box sx={{ display: tab === "completed" ? undefined : "none" }}>{completed.element}</Box>
        </>
    );
}

const tabSx = {
    minHeight: 30, minWidth: 0, px: 1,
    fontSize: 12, textTransform: "none",
};
